/**
 * Cortex Trigger Connector — Mycelium → Cortex Autonomous Work Queue
 *
 * Subscribes to business domain events on the Mycelium backbone and
 * enqueues autonomous work items to the `cortex_tasks` PGMQ queue.
 *
 * This connector is the bridge between event-driven architecture and
 * Cortex autonomy. When a business event crosses a threshold (task overdue,
 * deal stalled, invoice unpaid) Cortex is woken WITHOUT a human prompt.
 *
 * The mycelium-worker Edge Function polls `cortex_tasks` and wakes the brain
 * for each `cortex_work_requested` work item (Synaptic pattern).
 *
 * @see supabase/functions/mycelium-worker/index.ts
 *
 * Design principles:
 * - Economy of Movement: existing events trigger Cortex — no new mutations needed
 * - Mycelium Protocol: pure subscriber — zero coupling to source services
 * - Every Cortex trigger is logged to cortex_task_log for full observability
 *
 * @module mycelium-cortex-trigger
 * @since 2026-04-03
 * @see supabase/functions/cortex-async-worker/index.ts
 * @see docs/protocols/mycelium/MYCELIUM_CORTEX_ARCHITECTURE.md
 */

import {
  myceliumEventBackbone,
  type MyceliumEvent,
} from "./mycelium-event-backbone.service";
import { getMyceliumTransport } from "./mycelium-transport-registry";
import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import { logger } from "../lib/logger";

// ============================================================================
// CONFIGURATION
// ============================================================================

/**
 * Map of domain+type → intent string.
 * Only events present in this map are forwarded to Cortex.
 */
const INTENT_MAP: Record<string, string> = {
  "task:task_overdue": "task_overdue_resolution",
  "task:task_blocked": "task_blocked_resolution",
  "sales:deal_stalled": "deal_stalled_followup",
  "sales:opportunity_at_risk": "opportunity_risk_mitigation",
  "accounting:invoice_overdue": "invoice_overdue_chase",
  "accounting:invoice_overdue_30d": "invoice_overdue_escalation",
  "system:morning_brief_requested": "morning_brief_generation",
  "system:weekly_planning_requested": "weekly_planning_generation",
  "user:daily_summary_requested": "daily_summary_generation",
};

/** Domains that this connector listens to */
const TRIGGER_DOMAINS = ["task", "sales", "accounting", "system", "user"] as const;

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Derive the Cortex intent string from a Mycelium event.
 * Returns undefined if the event is not a recognised trigger.
 */
function deriveIntent(event: MyceliumEvent): string | undefined {
  return INTENT_MAP[`${event.domain}:${event.type}`];
}

/**
 * Derive execution priority for a Cortex work item.
 * Accounting domain and morning briefs are high-priority.
 */
function derivePriority(event: MyceliumEvent): "high" | "normal" {
  if (event.domain === "accounting") return "high";
  if (event.domain === "system" && event.type === "morning_brief_requested") return "high";
  return "normal";
}

// ============================================================================
// LAZY SUPABASE SERVICE CLIENT
// ============================================================================

let _supabaseServiceClient: SupabaseClient | null = null;

function getServiceClient(): SupabaseClient {
  if (!_supabaseServiceClient) {
    const url = process.env.SUPABASE_URL ?? "";
    const key = process.env.SUPABASE_SERVICE_ROLE_KEY ?? "";
    _supabaseServiceClient = createClient(url, key);
  }
  return _supabaseServiceClient;
}

// ============================================================================
// CONNECTOR SERVICE
// ============================================================================

export class CortexTriggerConnector {
  private initialized = false;

  /**
   * Initialize connector and subscribe to business trigger events.
   * Listens across task/sales/accounting/system/user domains and
   * enqueues work items to the cortex_tasks PGMQ queue.
   */
  async initialize(): Promise<void> {
    if (this.initialized) {
      logger.debug("CortexTriggerConnector already initialized");
      return;
    }

    logger.info("🧠 Cortex Trigger connector initializing");

    myceliumEventBackbone.subscribe({
      name: "CortexTriggerConnector",
      domains: [...TRIGGER_DOMAINS],
      handler: this.handleEvent.bind(this),
      priority: 4, // After business connectors, before audit trail
    });

    this.initialized = true;
    logger.info(
      "✅ Cortex Trigger connector ready — business events will wake Cortex autonomously"
    );
  }

  /**
   * Handle a business event by deriving the Cortex intent, enqueuing
   * a work item to the cortex_tasks PGMQ queue, and writing an audit row
   * to cortex_task_log.
   */
  private async handleEvent(event: MyceliumEvent): Promise<void> {
    const intent = deriveIntent(event);

    if (!intent) {
      // Not a recognised trigger — skip silently
      return;
    }

    const priority = derivePriority(event);

    // 1. Enqueue to cortex_tasks via Mycelium transport
    let queueMsgId: number | undefined;
    try {
      const result = await getMyceliumTransport().publish({
        domain: "cortex",
        type: "cortex_work_requested",
        userId: event.userId,
        orgId: event.orgId,
        payload: {
          intent,
          triggerDomain: event.domain,
          triggerType: event.type,
          triggerEventId: event.id,
          triggerContext: event.payload,
          requestedAt: new Date().toISOString(),
          priority,
        },
      });

      if (!result.success) {
        logger.warn("CortexTrigger: failed to enqueue cortex work item", {
          intent,
          triggerEvent: `${event.domain}.${event.type}`,
          error: result.error,
        });
      } else {
        queueMsgId = typeof result.data === "number" ? result.data : undefined;
        logger.info("CortexTrigger: work item enqueued", {
          intent,
          triggerEvent: `${event.domain}.${event.type}`,
          priority,
          messageId: result.data,
        });
      }
    } catch (error) {
      logger.error("CortexTrigger: error enqueueing cortex work item", {
        intent,
        triggerEvent: `${event.domain}.${event.type}`,
        error: error instanceof Error ? error.message : String(error),
      });
      // Don't rethrow — Cortex triggering is non-blocking
    }

    // 2. Insert audit row into cortex_task_log
    try {
      const { error: dbError } = await getServiceClient()
        .from("cortex_task_log")
        .insert({
          trigger_event_domain: event.domain,
          trigger_event_type: event.type,
          trigger_event_id: event.id,
          user_id: event.userId,
          org_id: event.orgId,
          intent,
          context: event.payload ?? {},
          status: "queued",
          queue_msg_id: queueMsgId,
        });

      if (dbError) {
        logger.warn("CortexTrigger: failed to insert cortex_task_log row", {
          intent,
          error: dbError.message,
        });
      }
    } catch (error) {
      logger.error("CortexTrigger: error writing cortex_task_log", {
        intent,
        error: error instanceof Error ? error.message : String(error),
      });
      // Don't rethrow — audit log write is best-effort
    }
  }

  /**
   * Dispose the connector.
   */
  async dispose(): Promise<void> {
    this.initialized = false;
    logger.info("🧹 Cortex Trigger connector disposed");
  }
}

// ============================================================================
// SINGLETON
// ============================================================================

export const cortexTriggerConnector = new CortexTriggerConnector();
